import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '@features/clinics/services/clinicApi';
import { createClinicHierarchy } from '../services/clinicHierarchyApi';
import { US_STATES } from '@shared/constants/us-data';
import './AddClinicModal.css';

export interface AddClinicInitialData {
  name?: string;
  phone?: string;
  email?: string;
  street?: string;
  suite?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface AddClinicModalProps {
  parentClinicId: number | null;
  onClose: () => void;
  onSaved: (clinicId: number) => void;
  initialData?: AddClinicInitialData;
}

export default function AddClinicModal({ parentClinicId, onClose, onSaved, initialData }: Readonly<AddClinicModalProps>) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: initialData?.name ?? '',
    phone: initialData?.phone ?? '',
    email: initialData?.email ?? '',
    street: initialData?.street ?? '',
    suite: initialData?.suite ?? '',
    city: initialData?.city ?? '',
    state: initialData?.state ?? '',
    zip: initialData?.zip ?? '',
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const hasAddress = !!(formData.street || formData.city || formData.state || formData.zip);
      // No `code` is sent — usp_CreateClinic assigns the next sequential
      // clinic code server-side when none is supplied (SLV2-2819).
      const clinicResult = await clinicApi.createClinic({
        name: formData.name.trim(),
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        includeAddress: hasAddress,
        address1: formData.street.trim() || undefined,
        address2: formData.suite.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state || undefined,
        zip: formData.zip.trim() || undefined,
      });
      if (parentClinicId != null) {
        try {
          await createClinicHierarchy({
            clinicId: clinicResult.id,
            parentClinicId,
          });
        } catch {
          queryClient.invalidateQueries({ queryKey: ['unassignedClinics'] });
          throw new Error(
            'Clinic was created but could not be placed in the hierarchy. ' +
            'It will appear in the Unassigned Clinics panel.'
          );
        }
      } else {
        queryClient.invalidateQueries({ queryKey: ['unassignedClinics'] });
      }
      return clinicResult.id;
    },
    onSuccess: (clinicId) => onSaved(clinicId),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    mutation.mutate();
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-add-clinic">
        <h2>Add Clinic</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="clinicName">Clinic Name *</label>
            <input
              type="text"
              id="clinicName"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter clinic name"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="clinicPhone">Main Phone</label>
              <input
                type="tel"
                id="clinicPhone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 555-5555"
                maxLength={20}
              />
            </div>
            <div className="form-group">
              <label htmlFor="clinicEmail">Email</label>
              <input
                type="email"
                id="clinicEmail"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="clinic@example.com"
                maxLength={255}
              />
            </div>
          </div>

          <h3 className="modal-form-section-heading">
            Mailing Address
          </h3>

          <div className="form-group">
            <label htmlFor="clinicStreet">Street</label>
            <input
              type="text"
              id="clinicStreet"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="123 Main St"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="clinicSuite">Suite</label>
            <input
              type="text"
              id="clinicSuite"
              name="suite"
              value={formData.suite}
              onChange={handleChange}
              placeholder="Suite 100"
              maxLength={200}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="clinicCity">City</label>
              <input
                type="text"
                id="clinicCity"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="clinicState">State</label>
              <select
                id="clinicState"
                name="state"
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group form-group-half">
            <label htmlFor="clinicZip">ZIP Code</label>
            <input
              type="text"
              id="clinicZip"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              placeholder="12345"
              maxLength={10}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending || !formData.name.trim()}
            >
              {mutation.isPending ? 'Creating...' : 'Create Clinic'}
            </button>
          </div>
          {mutation.isError && (
            <p className="error modal-error-message">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Error creating clinic. Please try again.'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
